import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import dayjs from 'dayjs';
import {
  GetAttendanceAudit,
  IAttendanceAuditEntry,
} from '../../../services/technician';
import { getComputedStatusLabel } from '../../../utils/attendanceDisplay';

type Props = {
  technicianId: string;
  /** DD-MM-YYYY — scope the log to one attendance day */
  date?: string;
};

const ACTION_LABELS: Record<string, string> = {
  STATUS_CHANGE: 'Status changed',
  PUNCH_ADDED: 'Punch added',
  PUNCH_EDITED: 'Punch edited',
  MANUAL_OVERRIDE: 'Manual override',
  FINALIZED: 'Day finalized',
};

const actorLabel = (entry: IAttendanceAuditEntry): string => {
  const kind = entry.changedBy?.kind || 'SYSTEM';
  if (kind === 'ADMIN') {
    const admin = entry.changedBy?.id;
    if (admin && typeof admin === 'object' && admin.name) return admin.name;
    return 'Admin';
  }
  if (kind === 'TECHNICIAN') return 'Technician';
  return 'System';
};

/** Compact who/when/from→to timeline of attendance changes for one day. */
export const AttendanceAuditLog: React.FC<Props> = ({ technicianId, date }) => {
  const [entries, setEntries] = useState<IAttendanceAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const result = await GetAttendanceAudit({ technicianId, date });
        if (!cancelled) setEntries(result.items);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load history');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [technicianId, date]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={18} sx={{ color: '#E31E25' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="caption" color="#f87171">
        {error}
      </Typography>
    );
  }

  if (!entries.length) {
    return (
      <Typography variant="caption" color="#6b7280">
        No changes recorded yet.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {entries.map((entry) => (
        <Box
          key={entry._id}
          sx={{
            pl: 1.25,
            borderLeft: '2px solid #333',
          }}
        >
          <Typography variant="caption" sx={{ display: 'block', color: '#d1d5db' }}>
            {ACTION_LABELS[entry.action] || entry.action}
            {entry.fromStatus || entry.toStatus ? (
              <>
                {': '}
                <Box component="span" sx={{ color: '#9ca3af' }}>
                  {getComputedStatusLabel(entry.fromStatus) || '—'}
                </Box>
                {' → '}
                <Box component="span" sx={{ color: '#f3f4f6', fontWeight: 600 }}>
                  {getComputedStatusLabel(entry.toStatus) || '—'}
                </Box>
              </>
            ) : null}
          </Typography>
          <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.68rem' }}>
            {actorLabel(entry)} · {dayjs(entry.createdAt).format('DD MMM YYYY, hh:mm A')}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default AttendanceAuditLog;
